import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography,
  IconButton,
  Avatar,
  useMediaQuery,
  useTheme,
  Divider,
  CircularProgress,
  Autocomplete,
  TextField,
  Chip,
} from "@mui/material";
import { Close, ThumbUpOutlined } from "@mui/icons-material";
import { debounce } from "lodash";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { GET_JOBS_ACTIVE_DROPDOWN_URL, COMPANY_LISTING_LITE_URL } from "../../redux/api/config";

export interface CompanyBasicInfo {
  id?: number;
  companyId?: number;
  company_id?: number;
  companyName?: string;
  company_name?: string;
  name?: string;
}

export interface JobBasicInfo {
  jobId?: number | string;
  title?: string;
  jobTitle?: string;
  jobName?: string;
  job_name?: string;
  companyId?: number | null;
  company_id?: number;
  companyName?: string;
  company_name?: string;
  jobRole?: string | string[];
  job_role?: string | string[];
  status?: string | number;
}

export interface CandidateProfileInfo {
  fullName?: string;
  email?: string;
  profilePicturePath?: string;
  profileImage?: string;
}

interface ConfirmRecommendPopupProps {
  open: boolean;
  onClose: () => void;
  /** Called with selected job id (number) when user confirms */
  onConfirm: (selectedJobId: number) => void;
  /** List of companies (optional - if not provided, derived from jobs or fetched) */
  companies?: CompanyBasicInfo[];
  /** List of active jobs to choose from (e.g. organization jobs filtered by active) */
  jobs: JobBasicInfo[];
  loadingJobs?: boolean;
  /** Pre-select this job when opening (e.g. current page job) */
  defaultJobId?: number | string | null;
  candidates: CandidateProfileInfo[];
  isSubmitting?: boolean;
}

const getJobLabel = (job: JobBasicInfo) =>
  job?.title || job?.jobTitle || job?.jobName || job?.job_name || "Untitled Job";

const getJobDisplayLabel = (job: JobBasicInfo) =>
  job?.companyName || job?.company_name
    ? `${getJobLabel(job)} · ${job.companyName ?? job.company_name}`
    : getJobLabel(job);

const getJobRoles = (job: JobBasicInfo): string[] => {
  const r = job?.jobRole ?? job?.job_role;
  if (!r) return [];
  if (Array.isArray(r)) {
    return r.map((item: any) =>
      typeof item === "string" ? item : (item?.name ?? item?.label ?? item?.value ?? String(item))
    ).filter(Boolean);
  }
  if (typeof r === "string") return r.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
};

const getJobId = (job: JobBasicInfo): number =>
  typeof job.jobId === "number" ? job.jobId : parseInt(String(job.jobId || "0"), 10);

/** Map active dropdown API result item to JobBasicInfo */
const mapDropdownItemToJob = (r: any): JobBasicInfo => ({
  jobId: r.jobId ?? r.job_id,
  title: r.jobName ?? r.job_name ?? r.title ?? r.jobTitle,
  jobTitle: r.jobName ?? r.job_name ?? r.jobTitle,
  jobName: r.jobName ?? r.job_name,
  companyId: r.companyId ?? r.company_id,
  companyName: r.companyName ?? r.company_name,
  jobRole: r.jobRole ?? r.job_role ?? r.jobRoles ?? r.job_roles ?? r.role,
  job_role: r.job_role ?? r.jobRole ?? r.job_roles ?? r.jobRoles,
  status: 1,
});

const SEARCH_DEBOUNCE_MS = 350;

const getCompanyId = (c: CompanyBasicInfo): number =>
  typeof c.companyId === "number" ? c.companyId
  : typeof c.company_id === "number" ? c.company_id
  : typeof c.id === "number" ? c.id
  : parseInt(String(c.id ?? c.company_id ?? c.companyId ?? "0"), 10);

const getCompanyName = (c: CompanyBasicInfo): string =>
  c?.companyName ?? c?.company_name ?? c?.name ?? "Unknown";

const ConfirmRecommendPopup: React.FC<ConfirmRecommendPopupProps> = ({
  open,
  onClose,
  onConfirm,
  companies: companiesProp,
  jobs,
  loadingJobs = false,
  defaultJobId,
  candidates,
  isSubmitting = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [companies, setCompanies] = useState<CompanyBasicInfo[]>(companiesProp ?? []);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyBasicInfo | null>(null);

  // Fetch company list when popup opens (if not provided)
  useEffect(() => {
    if (!open) return;
    if (companiesProp && companiesProp.length > 0) {
      setCompanies(companiesProp);
      return;
    }
    setLoadingCompanies(true);
    API_MIDDLEWARE(COMPANY_LISTING_LITE_URL, "GET", { "Content-Type": "application/json" })
      .then((res: any) => {
        const result = res?.result ?? res;
        const records = result?.records ?? result?.list ?? (Array.isArray(result) ? result : []);
        const list = records.map((r: any) => ({
          id: r.id ?? r.companyId ?? r.company_id,
          companyId: r.companyId ?? r.company_id ?? r.id,
          company_id: r.company_id ?? r.companyId ?? r.id,
          companyName: r.companyName ?? r.company_name ?? r.name,
          company_name: r.company_name ?? r.companyName ?? r.name,
          name: r.name ?? r.companyName ?? r.company_name,
        }));
        setCompanies(list.length > 0 ? list : []);
      })
      .catch(() => {
        // Fallback: derive companies from jobs
        const active = jobs.filter((j) => {
          const s = j.status;
          if (s === undefined || s === null) return true;
          if (typeof s === "string") return String(s).toUpperCase() === "ACTIVE" || s === "1";
          return Number(s) === 1;
        });
        const seen = new Set<number>();
        const list: CompanyBasicInfo[] = [];
        active.forEach((j) => {
          const cid = typeof j.companyId === "number" ? j.companyId : parseInt(String(j.companyId ?? "0"), 10);
          if (cid && !seen.has(cid)) {
            seen.add(cid);
            list.push({
              companyId: cid,
              company_id: cid,
              companyName: j.companyName ?? j.company_name,
              company_name: j.company_name ?? j.companyName,
            });
          }
        });
        setCompanies(list);
      })
      .finally(() => setLoadingCompanies(false));
  }, [open, companiesProp]);

  const baseActiveJobs = jobs.filter((j) => {
    const s = j.status;
    if (s === undefined || s === null) return true;
    if (typeof s === "string") return String(s).toUpperCase() === "ACTIVE" || s === "1";
    return Number(s) === 1;
  });

  const getJobCompanyId = (j: JobBasicInfo): number => {
    const id = j.companyId ?? j.company_id;
    return typeof id === "number" ? id : parseInt(String(id ?? "0"), 10);
  };

  const activeJobs = useMemo(() => {
    if (!selectedCompany) return baseActiveJobs;
    const cid = getCompanyId(selectedCompany);
    if (!cid) return baseActiveJobs;
    return baseActiveJobs.filter((j) => getJobCompanyId(j) === cid);
  }, [baseActiveJobs, selectedCompany]);

  const [selectedJobId, setSelectedJobId] = useState<number | "">("");
  const [selectedJob, setSelectedJob] = useState<JobBasicInfo | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [apiJobs, setApiJobs] = useState<JobBasicInfo[]>([]);
  const [companyJobs, setCompanyJobs] = useState<JobBasicInfo[]>([]);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [loadingCompanyJobs, setLoadingCompanyJobs] = useState(false);
  const prevOpenRef = useRef(false);

  // Fetch active jobs from dropdown API when user has typed at least 2 characters (only when NO company selected).
  const fetchActiveJobsListing = useMemo(
    () =>
      debounce((query: string) => {
        if (!query || query.trim().length < 2) {
          setLoadingSearch(false);
          return;
        }
        setLoadingSearch(true);
        const url = GET_JOBS_ACTIVE_DROPDOWN_URL(query.trim());
        API_MIDDLEWARE(url, "GET", { "Content-Type": "application/json" })
          .then((res: any) => {
            const result = res?.result ?? [];
            setApiJobs(Array.isArray(result) ? result.map(mapDropdownItemToJob) : []);
          })
          .catch(() => setApiJobs([]))
          .finally(() => setLoadingSearch(false));
      }, SEARCH_DEBOUNCE_MS),
    []
  );

  useEffect(() => {
    return () => fetchActiveJobsListing.cancel();
  }, [fetchActiveJobsListing]);

  // When company is selected, fetch jobs for that company via API.
  useEffect(() => {
    if (!selectedCompany) {
      setCompanyJobs([]);
      setLoadingCompanyJobs(false);
      return;
    }
    const cid = getCompanyId(selectedCompany);
    if (!cid) {
      setCompanyJobs([]);
      return;
    }
    setLoadingCompanyJobs(true);
    const url = GET_JOBS_ACTIVE_DROPDOWN_URL(undefined, cid);
    API_MIDDLEWARE(url, "GET", { "Content-Type": "application/json" })
      .then((res: any) => {
        const result = res?.result ?? [];
        setCompanyJobs(Array.isArray(result) ? result.map(mapDropdownItemToJob) : []);
      })
      .catch(() => setCompanyJobs([]))
      .finally(() => setLoadingCompanyJobs(false));
  }, [selectedCompany]);

  // When popup opens, reset search state.
  useEffect(() => {
    if (open) {
      setApiJobs([]);
      setCompanyJobs([]);
    }
  }, [open]);

  // Trigger API search only when company is NOT selected and user has typed 2+ chars.
  const selectedJobLabel = selectedJob
    ? (selectedJob.companyName ? `${getJobLabel(selectedJob)} · ${selectedJob.companyName}` : getJobLabel(selectedJob))
    : "";
  useEffect(() => {
    if (selectedCompany) {
      setApiJobs([]);
      return;
    }
    const q = searchInput.trim();
    if (q.length >= 2 && q !== selectedJobLabel.trim()) {
      fetchActiveJobsListing(q);
    } else if (q.length < 2) {
      setApiJobs([]);
    }
  }, [searchInput, selectedJobLabel, fetchActiveJobsListing, selectedCompany]);

  // Dropdown options:
  // - Company selected: use companyJobs, filter locally by searchInput (no API search, any chars)
  // - Company not selected: use apiJobs when 2+ chars typed (API), else activeJobs
  // Filter uses getJobDisplayLabel so selected job stays in list when searchInput matches its display
  const dropdownOptions = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    const isSearchingApi = q.length >= 2 && q !== selectedJobLabel.trim().toLowerCase();

    if (selectedCompany) {
      let list = companyJobs;
      if (q.length > 0) {
        list = list.filter((j) => getJobDisplayLabel(j).toLowerCase().includes(q));
      }
      if (selectedJob && !list.some((j) => getJobId(j) === getJobId(selectedJob))) {
        list = [selectedJob, ...list];
      }
      return list;
    }

    let list: JobBasicInfo[];
    if (isSearchingApi) {
      if (loadingSearch && apiJobs.length === 0) return [];
      list = apiJobs.filter((j) => getJobDisplayLabel(j).toLowerCase().includes(q));
    } else {
      list = activeJobs;
    }
    if (selectedJob && !list.some((j) => getJobId(j) === getJobId(selectedJob))) {
      list = [selectedJob, ...list];
    }
    return list;
  }, [searchInput, selectedJobLabel, activeJobs, apiJobs, companyJobs, loadingSearch, selectedCompany, selectedJob]);

  const showSearchHint = !selectedCompany && searchInput.trim().length > 0 && searchInput.trim().length < 2;

  // When company filter changes, clear job selection if current job is no longer in filtered list
  useEffect(() => {
    if (!selectedJob) return;
    const stillInList = dropdownOptions.some((j) => getJobId(j) === getJobId(selectedJob));
    if (!stillInList) {
      setSelectedJob(null);
      setSelectedJobId("");
      setSearchInput("");
    }
  }, [selectedCompany, dropdownOptions, selectedJob]);

  // Only set initial selection when popup opens (open: false -> true). Keep selection until dismiss.
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;
    const active = jobs.filter((j) => {
      const s = j.status;
      if (s === undefined || s === null) return true;
      if (typeof s === "string") return String(s).toUpperCase() === "ACTIVE" || s === "1";
      return Number(s) === 1;
    });
    const defaultId = defaultJobId != null ? (typeof defaultJobId === "number" ? defaultJobId : parseInt(String(defaultJobId), 10)) : null;
    const defaultJob = defaultId != null && !Number.isNaN(defaultId) ? active.find((j) => getJobId(j) === defaultId) ?? null : null;
    if (defaultJob) {
      setSelectedJobId(getJobId(defaultJob));
      setSelectedJob(defaultJob);
      const label = getJobLabel(defaultJob);
      setSearchInput(defaultJob.companyName ? `${label} · ${defaultJob.companyName}` : label);
      setSelectedCompany(null);
    } else if (active.length > 0) {
      const first = active[0];
      setSelectedJobId(getJobId(first));
      setSelectedJob(first);
      const label = getJobLabel(first);
      setSearchInput(first.companyName ? `${label} · ${first.companyName}` : label);
      setSelectedCompany(null);
    } else {
      setSelectedJobId("");
      setSelectedJob(null);
      setSearchInput("");
      setSelectedCompany(null);
    }
  }, [open, defaultJobId, jobs]);

  const hasCandidates = candidates && candidates.length > 0;

  const handleConfirm = () => {
    if (selectedJobId !== "" && !Number.isNaN(Number(selectedJobId))) {
      onConfirm(Number(selectedJobId));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: isMobile ? 0 : "16px",
          margin: isMobile ? 0 : "32px",
          maxHeight: isMobile ? "100vh" : "calc(100vh - 64px)",
        },
      }}
    >
      <DialogTitle
        sx={{
          padding: isMobile ? "16px 20px" : "24px 24px 0 24px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ThumbUpOutlined sx={{ fontSize: "28px", color: "#756DE9" }} />
          <Typography
            sx={{
              fontSize: isMobile ? "18px" : "20px",
              fontWeight: "600",
              fontFamily: "DM Sans",
              color: "#1F2235",
            }}
          >
            Confirm Recommend Job
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: "#666",
            padding: "8px",
            "&:hover": {
              backgroundColor: "#f5f5f5",
            },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: isMobile ? "16px 20px 24px 20px" : "24px" }}>
        {/* Company selection */}
        {companies.length > 0 && (
          <Box sx={{ marginBottom: "16px" }}>
            <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "600",
                color: "#756DE9",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "8px",
                fontFamily: "DM Sans",
              }}
            >
              Company
            </Typography>
            {loadingCompanies ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
                <CircularProgress size={24} sx={{ color: "#756DE9" }} />
                <Typography variant="body2" sx={{ color: "#666" }}>Loading companies...</Typography>
              </Box>
            ) : (
              <Autocomplete
                value={selectedCompany}
                onChange={(_, newValue: CompanyBasicInfo | null) => setSelectedCompany(newValue)}
                options={companies}
                getOptionLabel={(option) => getCompanyName(option)}
                isOptionEqualToValue={(option, value) => getCompanyId(option) === getCompanyId(value)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="All companies"
                    size="small"
                    label="Company"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#F5F4FF",
                        borderRadius: "8px",
                        fontFamily: "DM Sans",
                        "& fieldset": { borderColor: "#B7B3EB" },
                        "&:hover fieldset": { borderColor: "#756DE9" },
                        "&.Mui-focused fieldset": { borderColor: "#756DE9" },
                      },
                    }}
                  />
                )}
                sx={{ fontFamily: "DM Sans" }}
              />
            )}
          </Box>
        )}

        {/* Job selection */}
        <Box sx={{ marginBottom: "16px" }}>
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: "600",
              color: "#756DE9",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "8px",
              fontFamily: "DM Sans",
            }}
          >
            Select job to recommend
          </Typography>
          {loadingJobs || (selectedCompany && loadingCompanyJobs) ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}>
              <CircularProgress size={24} sx={{ color: "#756DE9" }} />
              <Typography variant="body2" sx={{ color: "#666" }}>Loading jobs...</Typography>
            </Box>
          ) : (
            <Autocomplete
              value={selectedJob}
              inputValue={searchInput}
              onInputChange={(_, value) => setSearchInput(value)}
              onChange={(_, newValue: JobBasicInfo | null) => {
                setSelectedJob(newValue);
                setSelectedJobId(newValue ? getJobId(newValue) : "");
                if (newValue) {
                  const label = getJobLabel(newValue);
                  setSearchInput(newValue.companyName ? `${label} · ${newValue.companyName}` : label);
                }
              }}
              options={dropdownOptions}
              filterOptions={(x) => x}
              getOptionLabel={(option) => {
                const label = getJobLabel(option);
                return option.companyName ? `${label} · ${option.companyName}` : label;
              }}
              isOptionEqualToValue={(option, value) => getJobId(option) === getJobId(value)}
              loading={selectedCompany ? loadingCompanyJobs : loadingSearch}
              renderOption={(props, option) => {
                const jobRoles = getJobRoles(option);
                return (
                  <li {...props}>
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%", py: 0.5 }}>
                      <Typography sx={{ fontWeight: 600, color: "#1F2235", fontSize: "14px" }}>
                        {getJobLabel(option)}
                      </Typography>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {option.companyName && (
                          <Typography sx={{ fontSize: "13px", color: "#666" }}>
                            {option.companyName}
                          </Typography>
                        )}
                        {jobRoles.length > 0 && (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {jobRoles.map((role) => (
                              <Chip
                                key={role}
                                label={role}
                                size="small"
                                sx={{
                                  height: "22px",
                                  fontSize: "11px",
                                  backgroundColor: "#E8F5E9",
                                  color: "#2E7D32",
                                  borderColor: "#A5D6A7",
                                  "& .MuiChip-label": { px: 1 },
                                }}
                                variant="outlined"
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </li>
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={selectedCompany ? "Search to filter jobs" : "Search by job name or company (type 2+ characters)..."}
                  size="small"
                  label="Job"
                  helperText={showSearchHint ? "Type at least 2 characters to search jobs" : undefined}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#F5F4FF",
                      borderRadius: "8px",
                      fontFamily: "DM Sans",
                      "& fieldset": { borderColor: "#B7B3EB" },
                      "&:hover fieldset": { borderColor: "#756DE9" },
                      "&.Mui-focused fieldset": { borderColor: "#756DE9" },
                    },
                  }}
                />
              )}
              sx={{ fontFamily: "DM Sans" }}
            />
          )}
          {!loadingJobs && !(selectedCompany && loadingCompanyJobs) && !selectedCompany && activeJobs.length === 0 && searchInput.trim().length < 2 && (
            <Typography variant="body2" sx={{ color: "#666", mt: 1, fontFamily: "DM Sans" }}>
              No active jobs found. Create or activate a job first.
            </Typography>
          )}
          {selectedCompany && !loadingCompanyJobs && companyJobs.length === 0 && (
            <Typography variant="body2" sx={{ color: "#666", mt: 1, fontFamily: "DM Sans" }}>
              No active jobs found for this company.
            </Typography>
          )}
          {!selectedCompany && searchInput.trim().length >= 2 && !loadingSearch && dropdownOptions.length === 0 && (
            <Typography variant="body2" sx={{ color: "#666", mt: 1, fontFamily: "DM Sans" }}>
              No jobs match your search.
            </Typography>
          )}
          {selectedCompany && searchInput.trim().length > 0 && !loadingCompanyJobs && dropdownOptions.length === 0 && (
            <Typography variant="body2" sx={{ color: "#666", mt: 1, fontFamily: "DM Sans" }}>
              No jobs match your filter.
            </Typography>
          )}
        </Box>

        {/* Selected candidates list */}
        <Box
          sx={{
            marginBottom: "16px",
          }}
        >
          <Typography
            sx={{
              fontSize: "12px",
              fontWeight: "600",
              color: "#756DE9",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              marginBottom: "8px",
              fontFamily: "DM Sans",
            }}
          >
            Recommended candidates ({candidates?.length || 0})
          </Typography>
          <Box
            sx={{
              backgroundColor: "#F8F9FA",
              border: "1px solid #E9EDF4",
              borderRadius: "12px",
              maxHeight: "200px",
              overflowY: "auto",
              padding: "8px",
            }}
          >
            {hasCandidates ? (
              candidates.map((c, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                    padding: "10px 8px",
                    borderRadius: "8px",
                    "&:not(:last-child)": { borderBottom: "1px solid #E9EDF4" },
                  }}
                >
                  <Avatar
                    src={c.profilePicturePath || c.profileImage}
                    alt={c.fullName || "Candidate"}
                    sx={{
                      width: 44,
                      height: 44,
                      border: "2px solid #E0E0E0",
                      flexShrink: 0,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "15px",
                        fontWeight: "600",
                        color: "#1F2235",
                        fontFamily: "DM Sans",
                      }}
                    >
                      {c.fullName || "Candidate"}
                    </Typography>
                    {c.email && (
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#666",
                          marginTop: "2px",
                          fontFamily: "DM Sans",
                        }}
                      >
                        {c.email}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))
            ) : (
              <Typography sx={{ fontSize: "14px", color: "#666", padding: "12px", fontFamily: "DM Sans" }}>
                No candidates selected
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ marginY: "24px" }} />

        <Box
          sx={{
            display: "flex",
            gap: "12px",
            justifyContent: "flex-end",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Button
            onClick={onClose}
            disabled={isSubmitting}
            variant="outlined"
            sx={{
              textTransform: "none",
              fontFamily: "DM Sans",
              fontWeight: "600",
              borderRadius: "8px",
              borderColor: "#B7B3EB",
              color: "#756DE9",
              "&:hover": {
                borderColor: "#756DE9",
                backgroundColor: "#F5F4FF",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || selectedJobId === ""}
            variant="contained"
            sx={{
              textTransform: "none",
              fontFamily: "DM Sans",
              fontWeight: "600",
              borderRadius: "8px",
              backgroundColor: "#756DE9",
              minWidth: "180px",
              "&:hover": {
                backgroundColor: "#6B5CE6",
              },
            }}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ color: "white", mr: 1 }} />
                Recommending...
              </>
            ) : (
              "Confirm & Recommend"
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmRecommendPopup;
